import React, { useEffect, useMemo, useState } from "react";

const FinanceForm = ({
    finance = null,
    rendezVous = [],
    onSubmit,
    onCancel,
    loading: externalLoading = false
}) => {

    // =====================================================
    // STATE
    // =====================================================

    const [formData, setFormData] = useState({
        client_id: "",
        rendez_vous_id: "",
        prix_ht: "",
        cout_matiere: "",
        main_oeuvre: ""
    });

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const isLoading = loading || externalLoading;


    // =====================================================
    // RDV FINANCE ACTUELLE
    // =====================================================

    const rdvFinance = useMemo(() => {

        if (!finance || !Array.isArray(rendezVous)) {
            return null;
        }

        return rendezVous.find(
            (rdv) =>
                Number(rdv.id) ===
                Number(finance.rendez_vous_id)
        ) || null;

    }, [finance, rendezVous]);


    // =====================================================
    // INITIALISATION
    // =====================================================

    useEffect(() => {

        if (finance) {

            setFormData({

                client_id:
                    finance.client_id
                        ? String(finance.client_id)
                        : rdvFinance?.client_id
                            ? String(rdvFinance.client_id)
                            : "",

                rendez_vous_id:
                    finance.rendez_vous_id
                        ? String(finance.rendez_vous_id)
                        : "",

                prix_ht:
                    finance.prix_ht ?? "",

                cout_matiere:
                    finance.cout_matiere ?? "",

                main_oeuvre:
                    finance.main_oeuvre ?? ""
            });

        } else {

            setFormData({
                client_id: "",
                rendez_vous_id: "",
                prix_ht: "",
                cout_matiere: "",
                main_oeuvre: ""
            });
        }

        setError("");

    }, [finance, rdvFinance]);


    // =====================================================
    // CHANGEMENT CHAMPS
    // =====================================================

    const handleChange = (e) => {

        const {
            name,
            value
        } = e.target;

        setFormData((prev) => ({
            ...prev,
            [name]: value
        }));

        setError("");
    };


    // =====================================================
    // RENDEZ-VOUS DISPONIBLES
    // =====================================================

    const rendezVousDisponibles = useMemo(() => {

        if (!Array.isArray(rendezVous)) {
            return [];
        }

        return rendezVous.filter((rdv) => {

            // En modification :
            // toujours conserver le RDV de la finance actuelle
            if (
                finance &&
                Number(finance.rendez_vous_id) ===
                Number(rdv.id)
            ) {
                return true;
            }

            // En création :
            // uniquement les RDV qui n'ont pas encore de finance
            return !rdv.finance_id;

        });

    }, [rendezVous, finance]);


    // =====================================================
    // CLIENTS UNIQUES
    // =====================================================

    const clientsDisponibles = useMemo(() => {

        const map = new Map();

        rendezVousDisponibles.forEach((rdv) => {

            if (
                rdv.client_id &&
                rdv.client
            ) {

                map.set(
                    String(rdv.client_id),
                    {
                        id: rdv.client_id,
                        nom: rdv.client
                    }
                );
            }

        });

        return Array.from(map.values())
            .sort((a, b) =>
                String(a.nom).localeCompare(
                    String(b.nom),
                    "fr",
                    {
                        sensitivity: "base"
                    }
                )
            );

    }, [rendezVousDisponibles]);


    // =====================================================
    // RDV FILTRES PAR CLIENT
    // =====================================================

    const rendezVousFiltres = useMemo(() => {

        if (!formData.client_id) {
            return [];
        }

        return rendezVousDisponibles.filter(
            (rdv) =>
                Number(rdv.client_id) ===
                Number(formData.client_id)
        );

    }, [
        formData.client_id,
        rendezVousDisponibles
    ]);


    // =====================================================
    // RDV SELECTIONNE
    // =====================================================

    const rdvSelectionne = useMemo(() => {

        if (!Array.isArray(rendezVous)) {
            return null;
        }

        return rendezVous.find(
            (rdv) =>
                Number(rdv.id) ===
                Number(formData.rendez_vous_id)
        ) || null;

    }, [
        rendezVous,
        formData.rendez_vous_id
    ]);


    // =====================================================
    // FORMAT DATE
    // =====================================================

    const formatDate = (date) => {

        if (!date) {
            return "-";
        }

        const d = new Date(date);

        if (Number.isNaN(d.getTime())) {
            return date;
        }

        return d.toLocaleDateString("fr-FR");
    };


    // =====================================================
    // FORMAT HEURE
    // =====================================================

    const formatHeure = (heure) => {

        if (!heure) {
            return "-";
        }

        return String(heure).substring(0, 5);
    };


    // =====================================================
    // CHANGEMENT CLIENT
    // =====================================================

    const handleClientChange = (e) => {

        const clientId = e.target.value;

        setFormData((prev) => ({
            ...prev,
            client_id: clientId,
            rendez_vous_id: ""
        }));

        setError("");
    };


    // =====================================================
    // CHANGEMENT RDV
    // =====================================================

    const handleRendezVousChange = (e) => {

        const rendezVousId = e.target.value;

        setFormData((prev) => ({
            ...prev,
            rendez_vous_id: rendezVousId
        }));

        setError("");
    };


    // =====================================================
    // CALCULS
    // =====================================================

    const prixHT =
        Number(formData.prix_ht) || 0;

    const coutMatiere =
        Number(formData.cout_matiere) || 0;

    const mainOeuvre =
        Number(formData.main_oeuvre) || 0;

    const tpsTvq =
        prixHT * 0.14975;

    const prixTTC =
        prixHT + tpsTvq;

    const profit =
        prixHT -
        coutMatiere -
        mainOeuvre;


    // =====================================================
    // SUBMIT
    // =====================================================

    const handleSubmit = async (e) => {

        e.preventDefault();

        setError("");


        // -------------------------------------------------
        // VALIDATION CLIENT
        // -------------------------------------------------

        if (!formData.client_id) {

            setError(
                "Veuillez sélectionner un client."
            );

            return;
        }


        // -------------------------------------------------
        // VALIDATION RDV
        // -------------------------------------------------

        if (!formData.rendez_vous_id) {

            setError(
                "Veuillez sélectionner un rendez-vous."
            );

            return;
        }


        // -------------------------------------------------
        // VALIDATION PRIX HT
        // -------------------------------------------------

        if (
            formData.prix_ht === "" ||
            Number(formData.prix_ht) < 0
        ) {

            setError(
                "Veuillez saisir un prix HT valide."
            );

            return;
        }


        // -------------------------------------------------
        // VALIDATION MATIERE
        // -------------------------------------------------

        if (
            formData.cout_matiere === "" ||
            Number(formData.cout_matiere) < 0
        ) {

            setError(
                "Veuillez saisir un coût matière valide."
            );

            return;
        }


        // -------------------------------------------------
        // VALIDATION MAIN D'OEUVRE
        // -------------------------------------------------

        if (
            formData.main_oeuvre === "" ||
            Number(formData.main_oeuvre) < 0
        ) {

            setError(
                "Veuillez saisir le coût de main-d'œuvre."
            );

            return;
        }


        try {

            setLoading(true);


            // -------------------------------------------------
            // DONNÉES ENVOYÉES AU BACKEND
            // -------------------------------------------------

            const data = {

                rendez_vous_id:
                    Number(formData.rendez_vous_id),

                prix_ht:
                    Number(formData.prix_ht),

                cout_matiere:
                    Number(formData.cout_matiere),

                main_oeuvre:
                    Number(formData.main_oeuvre)
            };


            console.log(
                "Finance envoyée :",
                data
            );


            await onSubmit(data);

        } catch (err) {

            console.error(
                "Erreur finance :",
                err
            );

            setError(
                err?.response?.data?.message ||
                err?.message ||
                "Erreur lors de l'enregistrement de la finance."
            );

        } finally {

            setLoading(false);
        }
    };


    // =====================================================
    // RENDER
    // =====================================================

    return (

        <div className="finance-form-container">


            {/* =================================================
                HEADER
            ================================================= */}

            <div className="finance-form-header">

                <div>

                    <h2>
                        {finance
                            ? "Modifier la finance"
                            : "Nouvelle finance"
                        }
                    </h2>

                    <p>
                        {finance
                            ? "Modifier les informations financières du rendez-vous"
                            : "Créer les informations financières d'un rendez-vous"
                        }
                    </p>

                </div>

                <div className="finance-form-icon">
                    💰
                </div>

            </div>


            {/* =================================================
                ERREUR
            ================================================= */}

            {error && (

                <div className="finance-alert finance-alert-danger">
                    ⚠️ {error}
                </div>

            )}


            <form onSubmit={handleSubmit}>


                {/* =================================================
                    SECTION RENDEZ-VOUS
                ================================================= */}

                <div className="finance-section">

                    <div className="finance-section-title">

                        <span>
                            📅
                        </span>

                        <div>

                            <h3>
                                Rendez-vous
                            </h3>

                            <p>
                                Sélectionnez le client et son rendez-vous
                            </p>

                        </div>

                    </div>


                    <div className="finance-grid">


                        {/* =================================================
                            CLIENT
                        ================================================= */}

                        <div className="form-group">

                            <label htmlFor="client_id">

                                Client{" "}

                                <span>
                                    *
                                </span>

                            </label>


                            <select
                                id="client_id"
                                name="client_id"
                                value={formData.client_id}
                                disabled={isLoading}
                                onChange={handleClientChange}
                                required
                            >

                                <option value="">
                                    -- Sélectionner un client --
                                </option>


                                {clientsDisponibles.map(
                                    (client) => (

                                        <option
                                            key={client.id}
                                            value={client.id}
                                        >
                                            {client.nom}
                                        </option>

                                    )
                                )}

                            </select>

                        </div>


                        {/* =================================================
                            RENDEZ-VOUS
                        ================================================= */}

                        <div className="form-group">

                            <label htmlFor="rendez_vous_id">

                                Rendez-vous{" "}

                                <span>
                                    *
                                </span>

                            </label>


                            <select
                                id="rendez_vous_id"
                                name="rendez_vous_id"
                                value={formData.rendez_vous_id}
                                onChange={handleRendezVousChange}
                                disabled={
                                    isLoading ||
                                    !formData.client_id
                                }
                                required
                            >

                                <option value="">

                                    {!formData.client_id
                                        ? "-- Sélectionnez d'abord un client --"
                                        : "-- Sélectionner un rendez-vous --"
                                    }

                                </option>


                                {rendezVousFiltres.length === 0 &&
                                    formData.client_id && (

                                        <option
                                            value=""
                                            disabled
                                        >
                                            Aucun rendez-vous disponible
                                        </option>

                                    )
                                }


                                {rendezVousFiltres.map(
                                    (rdv) => (

                                        <option
                                            key={rdv.id}
                                            value={rdv.id}
                                        >

                                            RDV #{rdv.id}

                                            {" - "}

                                            {formatDate(
                                                rdv.date_rdv
                                            )}

                                            {" - "}

                                            {formatHeure(
                                                rdv.heure_debut
                                            )}

                                        </option>

                                    )
                                )}

                            </select>

                        </div>

                    </div>


                    {/* =================================================
                        INFORMATIONS RDV
                    ================================================= */}

                    {rdvSelectionne && (

                        <div className="finance-rdv-card">

                            <div className="finance-rdv-header">

                                <strong>
                                    Informations du rendez-vous
                                </strong>

                                <span>
                                    RDV #{rdvSelectionne.id}
                                </span>

                            </div>


                            <div className="finance-rdv-grid">


                                <div>

                                    <small>
                                        Client
                                    </small>

                                    <strong>
                                        👤{" "}
                                        {rdvSelectionne.client || "-"}
                                    </strong>

                                </div>


                                <div>

                                    <small>
                                        Date
                                    </small>

                                    <strong>
                                        📅{" "}
                                        {formatDate(
                                            rdvSelectionne.date_rdv
                                        )}
                                    </strong>

                                </div>


                                <div>

                                    <small>
                                        Horaire
                                    </small>

                                    <strong>

                                        🕐{" "}

                                        {formatHeure(
                                            rdvSelectionne.heure_debut
                                        )}

                                        {" - "}

                                        {formatHeure(
                                            rdvSelectionne.heure_fin
                                        )}

                                    </strong>

                                </div>


                                <div>

                                    <small>
                                        État
                                    </small>

                                    <strong>
                                        {rdvSelectionne.etat_travaux || "-"}
                                    </strong>

                                </div>

                            </div>

                        </div>

                    )}

                </div>


                {/* =================================================
                    SECTION INFORMATIONS FINANCIÈRES
                ================================================= */}

                <div className="finance-section">

                    <div className="finance-section-title">

                        <span>
                            💵
                        </span>

                        <div>

                            <h3>
                                Informations financières
                            </h3>

                            <p>
                                Saisissez les coûts et le prix de prestation
                            </p>

                        </div>

                    </div>


                    <div className="finance-grid finance-grid-3">


                        {/* PRIX HT */}

                        <div className="form-group">

                            <label htmlFor="prix_ht">

                                Prix HT{" "}

                                <span>
                                    *
                                </span>

                            </label>


                            <div className="finance-input-money">

                                <input
                                    type="number"
                                    id="prix_ht"
                                    name="prix_ht"
                                    value={formData.prix_ht}
                                    onChange={handleChange}
                                    min="0"
                                    step="0.01"
                                    disabled={isLoading}
                                    required
                                    placeholder="0.00"
                                />

                                <span>
                                    CAD
                                </span>

                            </div>

                        </div>


                        {/* COUT MATIERE */}

                        <div className="form-group">

                            <label htmlFor="cout_matiere">

                                Coût matière{" "}

                                <span>
                                    *
                                </span>

                            </label>


                            <div className="finance-input-money">

                                <input
                                    type="number"
                                    id="cout_matiere"
                                    name="cout_matiere"
                                    value={formData.cout_matiere}
                                    onChange={handleChange}
                                    min="0"
                                    step="0.01"
                                    disabled={isLoading}
                                    required
                                    placeholder="0.00"
                                />

                                <span>
                                    CAD
                                </span>

                            </div>

                        </div>


                        {/* MAIN D'OEUVRE */}

                        <div className="form-group">

                            <label htmlFor="main_oeuvre">

                                Main-d'œuvre{" "}

                                <span>
                                    *
                                </span>

                            </label>


                            <div className="finance-input-money">

                                <input
                                    type="number"
                                    id="main_oeuvre"
                                    name="main_oeuvre"
                                    value={formData.main_oeuvre}
                                    onChange={handleChange}
                                    min="0"
                                    step="0.01"
                                    disabled={isLoading}
                                    required
                                    placeholder="0.00"
                                />

                                <span>
                                    CAD
                                </span>

                            </div>

                        </div>

                    </div>

                </div>


                {/* =================================================
                    RESULTATS
                ================================================= */}

                <div className="finance-results">

                    <div className="finance-result">

                        <span>
                            TPS / TVQ
                        </span>

                        <strong>

                            {tpsTvq.toLocaleString(
                                "fr-FR",
                                {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2
                                }
                            )}

                            {" CAD"}

                        </strong>

                    </div>


                    <div className="finance-result">

                        <span>
                            Prix TTC
                        </span>

                        <strong>

                            {prixTTC.toLocaleString(
                                "fr-FR",
                                {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2
                                }
                            )}

                            {" CAD"}

                        </strong>

                    </div>


                    <div className="finance-result finance-result-profit">

                        <span>
                            Profit
                        </span>

                        <strong>

                            {profit.toLocaleString(
                                "fr-FR",
                                {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2
                                }
                            )}

                            {" CAD"}

                        </strong>

                    </div>

                </div>


                {/* =================================================
                    ACTIONS
                ================================================= */}

                <div className="finance-form-actions">

                    <button
                        type="button"
                        className="finance-btn finance-btn-cancel"
                        onClick={onCancel}
                        disabled={isLoading}
                    >
                        Annuler
                    </button>


                    <button
                        type="submit"
                        className="finance-btn finance-btn-submit"
                        disabled={isLoading}
                    >

                        {isLoading ? (

                            <>
                                <span className="finance-spinner"></span>
                                Enregistrement...
                            </>

                        ) : (

                            <>
                                💾{" "}

                                {finance
                                    ? "Modifier la finance"
                                    : "Enregistrer la finance"
                                }
                            </>

                        )}

                    </button>

                </div>

            </form>

        </div>
    );
};


export default FinanceForm;